import React, { useEffect, useMemo, useState } from 'react';
import { Check, FileText, Loader2, Printer, Search, Users, X } from 'lucide-react';
import { isActiveInPeriod } from '../lib/employeePeriod';
import { sortEmployeesDefault } from '../lib/employeeSorter';
import { calculateEffectiveBreakMinutes } from '../lib/attendanceCalculation';
import { toISODate } from '../lib/types';
import type { AdminEmployee, AttendanceShiftType, MonthlyRecord } from '../types/models';

type PeriodMode = 'closing-15' | 'calendar-month';

type PrintableMonthlyRecord = MonthlyRecord & {
  status?: string | null;
};

type MonthlyPrintPeriod = {
  key: string;
  label: string;
  start: string;
  end: string;
  endExclusive: string;
  mode: PeriodMode;
};

type MonthlyPrintSummary = {
  workDays: number;
  attendanceCount: number;
  nightShiftCount: number;
  paidLeaveCount: number;
  actualMinutes: number;
  overtimeMinutes: number;
  averageMinutes: number;
  unconfirmedCount: number;
};

type MonthlyPrintPage = {
  employee: AdminEmployee;
  period: MonthlyPrintPeriod;
  records: PrintableMonthlyRecord[];
  summary: MonthlyPrintSummary;
};

type MonthlyPrintTabProps = {
  employees: AdminEmployee[];
  supabaseClient: any;
  selectedMonth: string;
  dayBreakMinutes: number;
  nightBreakMinutes: number;
  roundUpTime: (timeString: string | undefined, employeeId?: string, workDate?: string) => string | undefined;
  calculateActualMinutes: (
    clockIn: string | undefined,
    clockOut: string | undefined,
    breakMinutes: number,
    shiftType: AttendanceShiftType | string,
    options?: {
      baseDate?: string | null;
      clockInDate?: string | null;
      clockOutDate?: string | null;
      employeeId?: string;
      workDate?: string;
      applyDefaultBreak?: boolean;
    }
  ) => number;
  calculateOvertime: (record: MonthlyRecord) => number;
};

type SourceRow = {
  employee_code?: string | number | null;
  work_date?: string | null;
  duty_type?: string | null;
  shift_type?: string | null;
  clock_in_at?: string | null;
  clock_in?: string | null;
  clock_out_at?: string | null;
  clock_out?: string | null;
  break_minutes?: number | null;
  status?: string | null;
};

const SHIFT_TYPE_LABELS: Record<string, string> = {
  day: '日勤',
  night: '夜勤',
  dayoff: '公休',
  paid: '有給',
  holiday_work: '休日出勤'
};

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

const parseMonthKey = (monthKey: string): { year: number; monthIndex: number } | null => {
  const match = monthKey.match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    return null;
  }

  return { year, monthIndex };
};

const addMonths = (monthKey: string, count: number): string => {
  const parsed = parseMonthKey(monthKey);
  if (!parsed) return monthKey;
  const next = new Date(parsed.year, parsed.monthIndex + count, 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
};

const compareMonthKey = (a: string, b: string): number => a.localeCompare(b);

const buildPrintPeriod = (monthKey: string, mode: PeriodMode): MonthlyPrintPeriod | null => {
  const parsed = parseMonthKey(monthKey);
  if (!parsed) return null;

  const { year, monthIndex } = parsed;
  const monthLabel = `${year}年${monthIndex + 1}月`;

  if (mode === 'closing-15') {
    const startDate = new Date(year, monthIndex, 16);
    const endDate = new Date(year, monthIndex + 1, 15);
    const endExclusiveDate = new Date(year, monthIndex + 1, 16);
    return {
      key: `${monthKey}:closing-15`,
      label: `${monthLabel}度`,
      start: toISODate(startDate),
      end: toISODate(endDate),
      endExclusive: toISODate(endExclusiveDate),
      mode
    };
  }

  const startDate = new Date(year, monthIndex, 1);
  const endDate = new Date(year, monthIndex + 1, 0);
  const endExclusiveDate = new Date(year, monthIndex + 1, 1);
  return {
    key: `${monthKey}:calendar-month`,
    label: `${monthLabel}度`,
    start: toISODate(startDate),
    end: toISODate(endDate),
    endExclusive: toISODate(endExclusiveDate),
    mode
  };
};

const buildPrintPeriods = (startMonth: string, endMonth: string, mode: PeriodMode): MonthlyPrintPeriod[] => {
  if (!parseMonthKey(startMonth) || !parseMonthKey(endMonth)) {
    return [];
  }
  if (compareMonthKey(startMonth, endMonth) > 0) {
    return [];
  }

  const periods: MonthlyPrintPeriod[] = [];
  let cursor = startMonth;
  let guard = 0;

  while (compareMonthKey(cursor, endMonth) <= 0 && guard < 24) {
    const period = buildPrintPeriod(cursor, mode);
    if (period) {
      periods.push(period);
    }
    cursor = addMonths(cursor, 1);
    guard += 1;
  }

  return periods;
};

const formatDate = (dateIso: string): string => {
  const date = new Date(`${dateIso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateIso;
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
};

const formatDateWithWeekday = (dateIso: string): string => {
  const date = new Date(`${dateIso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateIso;
  return `${date.getMonth() + 1}/${date.getDate()}(${WEEKDAY_LABELS[date.getDay()]})`;
};

const formatMinutes = (minutes: number): string => {
  const safeMinutes = Number.isFinite(minutes) ? Math.max(0, Math.round(minutes)) : 0;
  const hours = Math.floor(safeMinutes / 60);
  const mins = safeMinutes % 60;
  return `${hours}時間${String(mins).padStart(2, '0')}分`;
};

const formatCount = (value: number): string => {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(1).replace(/\.0$/, '');
};

const toDisplayTime = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  if (value.includes(':')) {
    return value.slice(0, 5);
  }
  return value;
};

const extractISODatePart = (value: unknown): string | null => {
  if (!value) return null;
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text;
  }
  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) {
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
      return toISODate(parsed);
    }
    return text.slice(0, 10);
  }
  return null;
};

const extractTimeComponents = (value: string | null | undefined): { hours: number; minutes: number } | null => {
  if (!value) return null;
  const match = value.match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }
  return { hours, minutes };
};

const addDaysToISODate = (baseDate: string, days: number): string => {
  const base = new Date(`${baseDate}T00:00:00`);
  base.setDate(base.getDate() + days);
  return toISODate(base);
};

const buildDateRange = (startIso: string, endIso: string): string[] => {
  const start = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return [];
  }

  const dates: string[] = [];
  const cursor = new Date(start);
  while (cursor.getTime() <= end.getTime()) {
    dates.push(toISODate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const canClockOutRollOverToNextDay = (
  shiftType: AttendanceShiftType | string | null | undefined,
  clockIn: string | null | undefined,
  clockOut: string | null | undefined
): boolean => {
  if (!clockOut) return false;

  const outComponents = extractTimeComponents(clockOut);
  const inComponents = clockIn ? extractTimeComponents(clockIn) : null;
  const outMinutes = outComponents ? outComponents.hours * 60 + outComponents.minutes : null;
  const inMinutes = inComponents ? inComponents.hours * 60 + inComponents.minutes : null;

  if (outMinutes !== null && inMinutes !== null) return outMinutes < inMinutes;
  return shiftType === 'night' && !!outComponents && outComponents.hours < 12;
};

const determineClockOutDate = (
  baseDate: string,
  shiftType: AttendanceShiftType | string | null | undefined,
  clockIn: string | null | undefined,
  clockOut: string | null | undefined,
  providedDate: string | null | undefined
): string | null => {
  if (!clockOut) return providedDate ?? null;
  if (providedDate) return providedDate;
  const allowNextDay = canClockOutRollOverToNextDay(shiftType, clockIn, clockOut);
  const nextDay = addDaysToISODate(baseDate, 1);
  return allowNextDay ? nextDay : baseDate;
};

const getClosingAlignedMonthString = (todayIso: string, closingDay: string): string => {
  const [yearStr, monthStr, dayStr] = todayIso.split('-');
  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1;
  const day = Number(dayStr);

  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || !Number.isFinite(day)) {
    return `${yearStr}-${monthStr}`;
  }

  if (closingDay === 'month-end') {
    return `${yearStr}-${monthStr}`;
  }

  const closingDayNumber = Number(closingDay);
  if (!Number.isFinite(closingDayNumber) || closingDayNumber < 1) {
    return `${yearStr}-${monthStr}`;
  }

  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const effectiveClosingDay = Math.min(closingDayNumber, daysInMonth);
  const offset = day > effectiveClosingDay ? 1 : 0;
  const target = new Date(year, monthIndex + offset, 1);
  return `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}`;
};

const getShiftDayWeight = (shiftType: AttendanceShiftType | string | null | undefined): number => {
  switch (shiftType) {
    case 'dayoff':
      return 0;
    case 'paid':
      return 1;
    default:
      return 1;
  }
};

const getShiftAttendanceWeight = (shiftType: AttendanceShiftType | string | null | undefined): number => {
  switch (shiftType) {
    case 'dayoff':
    case 'paid':
      return 0;
    default:
      return 1;
  }
};

const getDayWeightForRecord = (
  record: MonthlyRecord,
  periodEndInclusive: string,
  closingDay: string
): number => {
  if (
    record.shiftType === 'night' &&
    record.clockOutDate &&
    record.clockOutDate > record.date
  ) {
    const startPeriod = getClosingAlignedMonthString(record.date, closingDay);
    const endPeriod = getClosingAlignedMonthString(record.clockOutDate, closingDay);
    if (startPeriod !== endPeriod) {
      return 1;
    }
    return record.clockOutDate <= periodEndInclusive ? 2 : 1;
  }
  return getShiftDayWeight(record.shiftType);
};

const isConfirmedStatus = (status: string | null | undefined): boolean => {
  const normalized = (status ?? '').toString().toLowerCase();
  if (!normalized) return false;
  return ['確定', 'completed', 'approved', '退勤済み', 'done'].some(keyword => normalized.includes(keyword));
};

const rowCrossesIntoPeriod = (row: SourceRow, period: MonthlyPrintPeriod): boolean => {
  const employeeCode = row.employee_code ? String(row.employee_code) : null;
  if (!employeeCode || !row.work_date) return false;

  const workDateIso = String(row.work_date);
  if (workDateIso >= period.start) return false;

  const shiftType = row.duty_type ?? row.shift_type ?? 'day';
  const clockOutRaw = row.clock_out_at ?? row.clock_out ?? null;

  const crossesBoundary = (() => {
    if (clockOutRaw) {
      const clockOutDate = extractISODatePart(clockOutRaw);
      if (clockOutDate) {
        return clockOutDate !== workDateIso;
      }
    }
    return shiftType === 'night';
  })();

  if (!crossesBoundary) return false;

  const nextDateIso = addDaysToISODate(workDateIso, 1);
  return nextDateIso >= period.start && nextDateIso < period.endExclusive;
};

const summarizeRecords = (
  records: PrintableMonthlyRecord[],
  period: MonthlyPrintPeriod,
  calculateOvertime: (record: MonthlyRecord) => number,
  carryInWorkDays: number
): MonthlyPrintSummary => {
  const closingDayForWeight = period.mode === 'closing-15' ? '15' : 'month-end';
  const workDaysFromRecords = records.reduce(
    (sum, record) => sum + getDayWeightForRecord(record, period.end, closingDayForWeight),
    0
  );
  const attendanceCount = records.reduce((sum, record) => sum + getShiftAttendanceWeight(record.shiftType), 0);
  const actualMinutes = records.reduce((sum, record) => sum + record.totalMinutes, 0);
  const overtimeMinutes = records.reduce((sum, record) => sum + Math.max(0, calculateOvertime(record)), 0);
  const workDays = workDaysFromRecords + carryInWorkDays;

  return {
    workDays,
    attendanceCount,
    nightShiftCount: records.filter(record => record.shiftType === 'night').length,
    paidLeaveCount: records.filter(record => record.shiftType === 'paid').length,
    actualMinutes,
    overtimeMinutes,
    averageMinutes: workDays > 0 ? Math.round(actualMinutes / workDays) : 0,
    unconfirmedCount: records.filter(record => !isConfirmedStatus(record.status)).length
  };
};

const MonthlyPrintTab = ({
  employees,
  supabaseClient,
  selectedMonth,
  dayBreakMinutes,
  nightBreakMinutes,
  roundUpTime,
  calculateActualMinutes,
  calculateOvertime
}: MonthlyPrintTabProps) => {
  const [periodMode, setPeriodMode] = useState<PeriodMode>('closing-15');
  const [startMonth, setStartMonth] = useState<string>(selectedMonth);
  const [endMonth, setEndMonth] = useState<string>(selectedMonth);
  const [employeeSearch, setEmployeeSearch] = useState<string>('');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [pages, setPages] = useState<MonthlyPrintPage[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [pendingAutoPrint, setPendingAutoPrint] = useState<boolean>(false);

  const periods = useMemo(
    () => buildPrintPeriods(startMonth, endMonth, periodMode),
    [startMonth, endMonth, periodMode]
  );

  const selectableEmployees = useMemo(() => {
    const periodStart = periods[0]?.start;
    const periodEnd = periods[periods.length - 1]?.end;
    const withEmployeeCode = employees.filter(employee => employee.employeeCode);

    if (!periodStart || !periodEnd) {
      return sortEmployeesDefault(withEmployeeCode.filter(employee => employee.status === 'active'));
    }

    return sortEmployeesDefault(
      withEmployeeCode.filter(employee =>
        employee.status === 'active' || isActiveInPeriod(employee, periodStart, periodEnd)
      )
    );
  }, [employees, periods]);

  const filteredEmployees = useMemo(() => {
    const keyword = employeeSearch.trim().toLowerCase();
    if (!keyword) return selectableEmployees;
    return selectableEmployees.filter(employee => {
      const code = employee.employeeCode ? String(employee.employeeCode).toLowerCase() : '';
      return employee.name.toLowerCase().includes(keyword) || code.includes(keyword);
    });
  }, [employeeSearch, selectableEmployees]);

  useEffect(() => {
    setSelectedEmployeeIds(prev =>
      prev.filter(employeeId => selectableEmployees.some(employee => employee.id === employeeId))
    );
  }, [selectableEmployees]);

  const selectedEmployees = useMemo(
    () => selectableEmployees.filter(employee => selectedEmployeeIds.includes(employee.id)),
    [selectableEmployees, selectedEmployeeIds]
  );

  const employeesByCode = useMemo(() => new Map(
    employees.filter(employee => employee.employeeCode).map(employee => [String(employee.employeeCode), employee])
  ), [employees]);

  const selectedEmployeeCount = selectedEmployeeIds.length;
  const printPageCount = pages.length;

  useEffect(() => {
    if (!pendingAutoPrint || loading || pages.length === 0) return undefined;

    const timeoutId = window.setTimeout(() => {
      setPendingAutoPrint(false);
      window.print();
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [pendingAutoPrint, loading, pages.length]);

  const toggleEmployee = (employeeId: string): void => {
    setSelectedEmployeeIds(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const selectAllFilteredEmployees = (): void => {
    setSelectedEmployeeIds(prev => {
      const next = new Set(prev);
      filteredEmployees.forEach(employee => next.add(employee.id));
      return Array.from(next);
    });
  };

  const clearSelectedEmployees = (): void => {
    setSelectedEmployeeIds([]);
  };

  const buildRecordFromRow = (row: SourceRow): { employeeCode: string; record: PrintableMonthlyRecord } | null => {
    const employeeCode = row.employee_code ? String(row.employee_code) : '';
    const workDateIso = row.work_date ? String(row.work_date) : '';
    if (!employeeCode || !workDateIso) return null;

    const shiftType = (row.duty_type ?? row.shift_type ?? 'day') as MonthlyRecord['shiftType'];
    const storedBreakMinutes = Number(row.break_minutes ?? 0);
    const employeeId = employeesByCode.get(employeeCode)?.id;
    const rawClockIn = toDisplayTime(row.clock_in_at ?? row.clock_in ?? null);
    const clockOut = toDisplayTime(row.clock_out_at ?? row.clock_out ?? null);
    const clockIn = (rawClockIn ? roundUpTime(rawClockIn, employeeId, workDateIso) : null) ?? rawClockIn;
    const clockInDateIso = clockIn ? workDateIso : null;
    const clockOutDateIso = determineClockOutDate(
      workDateIso,
      shiftType,
      rawClockIn,
      clockOut,
      extractISODatePart(row.clock_out_at ?? row.clock_out ?? null)
    );

    const rawMinutes = calculateActualMinutes(
      rawClockIn ?? undefined,
      clockOut ?? undefined,
      0,
      shiftType,
      {
        baseDate: workDateIso,
        clockInDate: clockInDateIso,
        clockOutDate: clockOutDateIso,
        employeeId,
        workDate: workDateIso,
        applyDefaultBreak: false
      }
    );
    const effectiveBreakMinutes = calculateEffectiveBreakMinutes(rawMinutes, storedBreakMinutes, shiftType, {
      dayBreakMinutes,
      nightBreakMinutes
    });
    const totalMinutes = shiftType === 'paid'
      ? 480
      : shiftType === 'dayoff'
        ? 0
        : rawMinutes > 0 ? Math.max(0, rawMinutes - effectiveBreakMinutes) : 0;

    return {
      employeeCode,
      record: {
        date: workDateIso,
        clockIn,
        clockOut,
        clockInDate: clockInDateIso,
        clockOutDate: clockOutDateIso,
        breakMinutes: effectiveBreakMinutes,
        totalMinutes,
        shiftType,
        status: row.status ?? null
      }
    };
  };

  const handleGenerate = async (autoPrint = true): Promise<void> => {
    setError(null);
    setGeneratedAt(null);
    setPages([]);
    setPendingAutoPrint(false);

    if (!supabaseClient) {
      setError('Supabaseの設定が見つかりません。');
      return;
    }
    if (periods.length === 0) {
      setError('対象期間を確認してください。');
      return;
    }
    if (selectedEmployees.length === 0) {
      setError('従業員を選択してください。');
      return;
    }

    const employeeCodes = selectedEmployees
      .map(employee => employee.employeeCode ? String(employee.employeeCode) : '')
      .filter(Boolean);
    if (employeeCodes.length === 0) {
      setError('従業員コードが設定されている従業員を選択してください。');
      return;
    }

    setLoading(true);
    try {
      const fetchStart = addDaysToISODate(periods[0].start, -1);
      const fetchEndExclusive = periods[periods.length - 1].endExclusive;

      const sourceRows: SourceRow[] = [];
      const pageSize = 500;
      for (let offset = 0; ; offset += pageSize) {
        const { data, error: fetchError } = await supabaseClient
          .from('attendance_records')
          .select('id, employee_code, work_date, duty_type, clock_in_at, clock_out_at, break_minutes, status')
          .in('employee_code', employeeCodes)
          .gte('work_date', fetchStart)
          .lt('work_date', fetchEndExclusive)
          .order('employee_code', { ascending: true })
          .order('work_date', { ascending: true })
          .order('clock_in_at', { ascending: true })
          .order('id', { ascending: true })
          .range(offset, offset + pageSize - 1);
        if (fetchError) throw fetchError;
        sourceRows.push(...(data ?? []));
        if ((data ?? []).length < pageSize) break;
      }
      const recordsByEmployeeCode = new Map<string, PrintableMonthlyRecord[]>();

      sourceRows.forEach(row => {
        const built = buildRecordFromRow(row);
        if (!built) return;
        const current = recordsByEmployeeCode.get(built.employeeCode) ?? [];
        current.push(built.record);
        recordsByEmployeeCode.set(built.employeeCode, current);
      });

      recordsByEmployeeCode.forEach(records => {
        records.sort((a, b) => a.date.localeCompare(b.date));
      });

      const nextPages: MonthlyPrintPage[] = [];

      selectedEmployees.forEach(employee => {
        periods.forEach(period => {
          const employeeCode = employee.employeeCode ? String(employee.employeeCode) : '';
          const allEmployeeRecords = recordsByEmployeeCode.get(employeeCode) ?? [];
          const periodRecords = allEmployeeRecords.filter(record =>
            record.date >= period.start && record.date < period.endExclusive
          );
          const carryInWorkDays = sourceRows.filter(row =>
            row.employee_code &&
            String(row.employee_code) === employeeCode &&
            rowCrossesIntoPeriod(row, period)
          ).length;

          nextPages.push({
            employee,
            period,
            records: periodRecords,
            summary: summarizeRecords(periodRecords, period, calculateOvertime, carryInWorkDays)
          });
        });
      });

      setGeneratedAt(new Date().toLocaleString('ja-JP'));
      setPages(nextPages);
      setPendingAutoPrint(autoPrint && nextPages.length > 0);
    } catch (err: any) {
      setPendingAutoPrint(false);
      setError(`印刷用データの取得に失敗しました: ${err?.message ?? String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (): void => {
    if (pages.length === 0) return;
    window.setTimeout(() => window.print(), 50);
  };

  const renderRecordValue = (record: PrintableMonthlyRecord | undefined, field: 'clockIn' | 'clockOut'): string => {
    if (!record) return '';
    return record[field] ?? '';
  };

  const renderActualMinutes = (record: PrintableMonthlyRecord | undefined): string => {
    if (!record) return '';
    if (record.shiftType === 'paid') return '8時間00分';
    if (record.shiftType === 'dayoff') return '';
    return formatMinutes(record.totalMinutes);
  };

  const renderBreakMinutes = (record: PrintableMonthlyRecord | undefined): string => {
    if (!record || record.shiftType === 'paid' || record.shiftType === 'dayoff') return '';
    return record.breakMinutes > 0 ? `${record.breakMinutes}分` : '';
  };

  const renderOvertimeMinutes = (record: PrintableMonthlyRecord | undefined): string => {
    if (!record) return '';
    const overtime = calculateOvertime(record);
    return overtime > 0 ? formatMinutes(overtime) : '';
  };

  return (
    <>
      <div className="space-y-6">
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">出力条件</h3>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">期間区分</label>
                <div className="inline-flex rounded-lg border border-gray-300 bg-gray-50 p-1">
                  <button
                    type="button"
                    onClick={() => setPeriodMode('closing-15')}
                    className={`px-4 py-2 text-sm font-medium rounded-md ${periodMode === 'closing-15'
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    16日始まり
                  </button>
                  <button
                    type="button"
                    onClick={() => setPeriodMode('calendar-month')}
                    className={`px-4 py-2 text-sm font-medium rounded-md ${periodMode === 'calendar-month'
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    1日始まり
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">開始月</label>
                  <input
                    type="month"
                    value={startMonth}
                    onChange={event => setStartMonth(event.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">終了月</label>
                  <input
                    type="month"
                    value={endMonth}
                    onChange={event => setEndMonth(event.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-xs font-medium text-blue-700">対象期間</p>
                    <p className="mt-1 font-semibold text-blue-950">
                      {periods.length > 0
                        ? `${formatDate(periods[0].start)} - ${formatDate(periods[periods.length - 1].end)}`
                        : '未設定'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-blue-700">月数</p>
                    <p className="mt-1 font-semibold text-blue-950">{periods.length}ヶ月</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-blue-700">選択従業員</p>
                    <p className="mt-1 font-semibold text-blue-950">{selectedEmployeeCount}名</p>
                  </div>
                </div>
              </div>

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleGenerate(true)}
                  disabled={loading || periods.length === 0 || selectedEmployeeCount === 0}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                  {loading ? '印刷準備中' : '印刷準備して印刷'}
                </button>
                <button
                  type="button"
                  onClick={handlePrint}
                  disabled={pages.length === 0 || loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
                >
                  <Printer className="h-4 w-4" />
                  再印刷
                </button>
                {generatedAt && (
                  <span className="text-sm text-gray-500">
                    作成日時: {generatedAt}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">従業員</h3>
              <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                {selectedEmployeeCount}/{selectableEmployees.length}
              </span>
            </div>
            <div className="p-4 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={employeeSearch}
                  onChange={event => setEmployeeSearch(event.target.value)}
                  placeholder="名前・従業員コード"
                  className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={selectAllFilteredEmployees}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Check className="h-3.5 w-3.5" />
                  全選択
                </button>
                <button
                  type="button"
                  onClick={clearSelectedEmployees}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  <X className="h-3.5 w-3.5" />
                  解除
                </button>
              </div>

              <div className="max-h-[460px] overflow-y-auto rounded-lg border border-gray-200">
                {filteredEmployees.map(employee => {
                  const checked = selectedEmployeeIds.includes(employee.id);
                  return (
                    <label
                      key={employee.id}
                      className="flex cursor-pointer items-center gap-3 border-b border-gray-100 px-3 py-2 last:border-b-0 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleEmployee(employee.id)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-gray-900">{employee.name}</span>
                        <span className="block text-xs text-gray-500">
                          {employee.employeeCode ? `コード ${employee.employeeCode}` : 'コード未設定'}
                          {employee.status === 'retired' && ' / 退職済み'}
                        </span>
                      </span>
                    </label>
                  );
                })}
                {filteredEmployees.length === 0 && (
                  <div className="px-4 py-8 text-center text-sm text-gray-500">
                    対象従業員がありません。
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-100 p-3">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">従業員</p>
                <p className="text-2xl font-bold text-gray-900">{selectedEmployeeCount}名</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-green-100 p-3">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">期間</p>
                <p className="text-2xl font-bold text-gray-900">{periods.length}ヶ月</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-gray-100 p-3">
                <Printer className="h-5 w-5 text-gray-700" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">印刷ページ</p>
                <p className="text-2xl font-bold text-gray-900">{printPageCount}枚</p>
              </div>
            </div>
          </div>
        </div>

        {pages.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">作成済みページ</h3>
              <span className="text-sm text-gray-500">{pages.length}枚</span>
            </div>
            <div className="divide-y divide-gray-100">
              {pages.slice(0, 8).map(page => (
                <div key={`${page.employee.id}-${page.period.key}`} className="flex items-center justify-between px-6 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{page.employee.name}</p>
                    <p className="text-xs text-gray-500">
                      {page.period.label} / {formatDate(page.period.start)} - {formatDate(page.period.end)}
                    </p>
                  </div>
                  <div className="text-right text-sm text-gray-700">
                    <p>実働 {formatMinutes(page.summary.actualMinutes)}</p>
                    <p className="text-xs text-gray-500">残業 {formatMinutes(page.summary.overtimeMinutes)}</p>
                  </div>
                </div>
              ))}
              {pages.length > 8 && (
                <div className="px-6 py-3 text-sm text-gray-500">
                  ほか {pages.length - 8}枚
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="monthly-print-output printable-report-root" aria-hidden="true">
        {pages.map(page => {
          const recordsByDate = new Map(page.records.map(record => [record.date, record]));
          const dates = buildDateRange(page.period.start, page.period.end);

          return (
            <section key={`${page.employee.id}-${page.period.key}`} className="monthly-print-page">
              <header className="monthly-print-header">
                <div>
                  <p className="monthly-print-kicker">有限会社さつきケアサービス</p>
                  <h1>勤務実績表</h1>
                </div>
                <div className="monthly-print-period">
                  <p>{page.period.label}</p>
                  <span>{formatDate(page.period.start)} - {formatDate(page.period.end)}</span>
                </div>
              </header>

              <div className="monthly-print-meta">
                <div>
                  <span>従業員コード</span>
                  <strong>{page.employee.employeeCode ?? ''}</strong>
                </div>
                <div>
                  <span>氏名</span>
                  <strong>{page.employee.name}</strong>
                </div>
                <div>
                  <span>雇用形態</span>
                  <strong>{page.employee.employmentType}</strong>
                </div>
                <div>
                  <span>職種</span>
                  <strong>{page.employee.jobType}</strong>
                </div>
              </div>

              <table className="monthly-print-summary">
                <tbody>
                  <tr>
                    <th>出勤日数</th>
                    <td>{formatCount(page.summary.workDays)}日</td>
                    <th>出勤回数</th>
                    <td>{formatCount(page.summary.attendanceCount)}回</td>
                    <th>夜勤回数</th>
                    <td>{formatCount(page.summary.nightShiftCount)}回</td>
                  </tr>
                  <tr>
                    <th>有給</th>
                    <td>{formatCount(page.summary.paidLeaveCount)}日</td>
                    <th>実働時間</th>
                    <td>{formatMinutes(page.summary.actualMinutes)}</td>
                    <th>残業時間</th>
                    <td>{formatMinutes(page.summary.overtimeMinutes)}</td>
                  </tr>
                </tbody>
              </table>

              <table className="monthly-print-table">
                <thead>
                  <tr>
                    <th>日付</th>
                    <th>勤務区分</th>
                    <th>出勤</th>
                    <th>退勤</th>
                    <th>休憩</th>
                    <th>実働</th>
                    <th>残業</th>
                    <th>確認</th>
                  </tr>
                </thead>
                <tbody>
                  {dates.map(date => {
                    const record = recordsByDate.get(date);
                    const shiftLabel = record ? SHIFT_TYPE_LABELS[record.shiftType] ?? record.shiftType ?? '' : '';
                    return (
                      <tr key={date}>
                        <td>{formatDateWithWeekday(date)}</td>
                        <td>{shiftLabel}</td>
                        <td>{renderRecordValue(record, 'clockIn')}</td>
                        <td>{renderRecordValue(record, 'clockOut')}</td>
                        <td>{renderBreakMinutes(record)}</td>
                        <td>{renderActualMinutes(record)}</td>
                        <td>{renderOvertimeMinutes(record)}</td>
                        <td />
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <footer className="monthly-print-footer">
                <span>作成日時: {generatedAt ?? ''}</span>
                <span>確認者: ____________________</span>
              </footer>
            </section>
          );
        })}
      </div>
    </>
  );
};

export default MonthlyPrintTab;
